import type {
  ChangeEvent,
  FocusEvent,
  DetailedHTMLProps,
  TextareaHTMLAttributes,
} from 'react';
import { forwardRef } from 'react';
import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';
import * as tokens from '@tokens';

import type { Interpolation, Theme } from '@emotion/react';

import { Icon } from '@components/Icon/Icon';
import { baseInput } from '../styles';

export interface TextareaProps
  extends Omit<
    DetailedHTMLProps<
      TextareaHTMLAttributes<HTMLTextAreaElement>,
      HTMLTextAreaElement
    >,
    'required' | 'readOnly' | 'disabled' | 'ref'
  > {
  /**
   * The unique ID (attribute) of the html textarea element.
   */
  id?: string;
  /**
   * Name (attribute) of the html textarea element.
   */
  name?: string;
  /**
   * Placeholder for the textarea element.
   */
  placeholder?: string;
  /**
   * Default value for the textarea element.
   */
  defaultValue?: string;
  /**
   * Specifies if the textarea is disabled.
   */
  isDisabled?: boolean;
  /**
   * Specifies if the textarea is invalid. When set to true, we'll render the textarea in invalid state.
   */
  isInvalid?: boolean;
  /**
   * Specifies if the textarea is required.
   */
  isRequired?: boolean;
  /**
   * Specifies if the textarea is in readonly.
   */
  isReadonly?: boolean;
  /**
   * Callback when the textarea value is changed.
   */
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  /**
   * Callback when the textarea is focused.
   */
  onFocus?: (event: FocusEvent<HTMLTextAreaElement>) => void;
  /**
   * Callback when the textarea looses focus.
   */
  onBlur?: (event: FocusEvent<HTMLTextAreaElement>) => void;
  /**
   * Number of visible lines of text.
   */
  rows?: number;
  /**
   * Styles override for the textarea element
   */
  textareaStyles?: Interpolation<Theme>;
  /**
   * Styles override for the textarea container
   */
  className?: string;
}

interface StyledTextareaProps {
  isInvalid: boolean;
  readOnly: boolean;
}

const StyledTextarea = styled('textarea', {
  shouldForwardProp: (prop: string) => isPropValid(prop),
})<StyledTextareaProps>`
  ${baseInput};
  max-width: 100%;
  min-height: ${tokens.spacer.x4};
  padding: ${tokens.spacer.x1} ${tokens.spacer.x5} ${tokens.spacer.x1}
    ${tokens.spacer.x2};
  ${tokens.transition('box-shadow')};
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
`;

const IconContainer = styled('span')`
  position: relative;
`;

const InputIcon = styled(Icon)`
  position: absolute;
  top: ${tokens.spacer.x1};
  right: ${tokens.spacer.x2};
  margin: auto;
  pointer-events: none;
`;

/**
 * A textarea allows users to enter or edit multiple lines of text.
 *
 * {@link https://zeroheight.com/76c439891/p/723ebc-textarea/b/218cd8 Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-textarea--basic-textarea Code documentation}
 *
 * @example
 * <Textarea defaultValue="My textarea" />
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      isDisabled,
      isInvalid,
      isReadonly,
      isRequired,
      rows,
      textareaStyles,
      className,
      ...props
    }: TextareaProps,
    ref,
  ): JSX.Element => (
    <Container className={className}>
      <StyledTextarea
        ref={ref}
        {...props}
        isInvalid={isInvalid}
        disabled={isDisabled}
        readOnly={isReadonly}
        required={isRequired}
        rows={rows}
        css={textareaStyles}
      />
      {isInvalid && (
        <IconContainer>
          <InputIcon icon="warning_alt" size="m" color="text_danger" />
        </IconContainer>
      )}
    </Container>
  ),
);

Textarea.displayName = 'Textarea';

Textarea.defaultProps = {
  rows: 2,
  isDisabled: false,
  isInvalid: false,
  isReadonly: false,
  isRequired: false,
};
